import { useEffect, useMemo } from "react"
import { toast } from "react-toastify"
import { TypedUseLazyQuery } from "@reduxjs/toolkit/query/react"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { ApiTypes, UITypes } from "types"

import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"

import { getSensorsTableHeaderCells } from "./getSensorsTableHeaderCells"

interface UseSensorsPageConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useLazyQuery: TypedUseLazyQuery<any, any, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  convertData: (data?: any) => UITypes.TableBodyRow[]
  listName: ApiTypes.Model.PageTableName
  tableName: string
  isVendor?: boolean
}

export const useSensorsPage = ({
  useLazyQuery,
  convertData,
  listName,
  tableName,
  isVendor = false,
}: UseSensorsPageConfig) => {
  const [triggerGetSensors, { isFetching, data }] = useLazyQuery()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const { urlQueryParams, originalQueryParams } = useUrlQueryParams()

  const sensors = data?.data || []

  const baseHeaderCells = useMemo(() => getSensorsTableHeaderCells({ isVendor }), [isVendor])
  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config ? reorderTableHeaderCells(baseHeaderCells, columnsData.data.config) : baseHeaderCells,
    [columnsData, baseHeaderCells],
  )
  const tableData = useMemo(() => convertData(sensors), [sensors, convertData])

  const hasActiveFilters = useMemo(
    () =>
      tableHeaderCells.some(
        cell => Object.hasOwn(originalQueryParams, cell.name || "") && originalQueryParams[cell.name || ""] !== "",
      ),
    [tableHeaderCells, originalQueryParams],
  )

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: data?.meta,
    fileName: "sensors",
    convertData,
    useLazyQuery,
  })

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetSensors(params)
      const error = getErrorMessage(res?.error)
      if (error) toast.error(error)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    sendRequest(urlQueryParams)
    triggerGetTableColumns({ list_name: listName })
  }, [listName, triggerGetSensors, triggerGetTableColumns, urlQueryParams])

  return {
    data: tableData,
    tableHeaderCells,
    isFetching,
    listName,
    tableName,
    meta: data?.meta,
    hasActiveFilters,
    onExport: handleExport,
  }
}
